// Wire contract for `GET /api/tools/launcher/campaigns/:id/members/by-contact/:contactId`
// (`mini-apps/launcher/event-manager-api.ts`) — one contact's membership on
// one campaign, joined with (optionally) its event-manager row. Consumed by
// U10's `CampaignMemberSidebar` (`mini-apps/_shared/components/`).
//
// Zod in a module the frontend imports is deliberate and has the same
// justification as `launcher-campaign-schema.ts` in this directory: the
// launcher HTTP API is a trust boundary for every other app
// (`mini-apps/CLAUDE.md` lane 2), and `.claude/team-conventions.md`'s
// "Schema-First Data Boundaries" forbids `as T` / `const x: T = await
// res.json()` there. This file is a dependency-free leaf (zod only, zero
// sibling imports), so it also type-checks cleanly under
// `tsc -p tsconfig.tests.json`'s commonjs config. See that file's fuller
// module doc for the full "why zod in a frontend-imported module" reasoning
// — not repeated here.
//
// ── Nullability, verified against Postgres, not against a hand-derived type ──
// Sources (none of these tables have a Prisma model — raw `$queryRaw` only):
//   `prisma/schema/migrations/20260317_launcher_sfdc_zz_campaign_members` —
//     `launcher_sfdc_campaign_members`. NOT NULL: id, campaign_id. Everything
//     else (`contact_id`, `cm_status`, `has_responded`, `created_date`,
//     `contact_name`, `contact_title`, `contact_email`, `account_name`,
//     `gtme_owner_name`, `gs_owner_name`, `claydr_name`, `sales_tier`,
//     `arr_active_subscriptions`, `suppressed`, `suppression_reasons`) is
//     NULLABLE.
//   `20260319_launcher_member_new_fields` adds `account_type` (nullable).
//   `20260413_launcher_event_manager` adds `product_tier`, `plan_interval`
//     (nullable) to the same table, plus creates `launcher_event_tables` and
//     `launcher_event_members` (`id` SERIAL NOT NULL; `campaign_id`,
//     `campaign_member_id` NOT NULL; everything else nullable/DEFAULTed, which
//     is not the same as NOT NULL).
//   `20260415_launcher_event_member_post_event` adds `primary_met_with`,
//     `secondary_met_with`, `tertiary_met_with`, `last_follow_up_date`
//     (nullable) to `launcher_event_members`.
//   `20260422120000_launcher_event_member_owner_fields` adds `owner_exclusion`,
//     `owner_priority` (nullable) to `launcher_event_members`.
//   `20260317_launcher_sfdc_tables` — `launcher_sfdc_campaigns`. NOT NULL:
//     id, name. `start_date` (DATE) is nullable.
//
// `arr_active_subscriptions` is `NUMERIC(12,2)`: `prisma.$queryRaw` hands
// that back as a Prisma `Decimal` object, which the route's raw SQL casts
// `::text` (same rule as every DATE/TIMESTAMPTZ column below) so the
// pre-serialization `safeParse` never sees a `Prisma.Decimal` — the wire
// value is always a plain string. `wireDecimal` stays a `number | string`
// union (rather than narrowing to just `string`) to match
// `launcher-campaign-schema.ts`'s `budgeted_cost`/`pipeline_goal`/`arr_goal`
// pattern for any caller that hand-constructs a payload with a numeric
// literal instead of going through the route's cast SQL.
// Every DATE/TIMESTAMPTZ column is read through an explicit `::text` cast in
// the route's SQL, so on the wire those are plain (nullable) strings, never
// a Date object needing its own parsing.
import { z } from 'zod';

/** `NUMERIC(12,2)` — a Prisma `Decimal` on the wire, i.e. a JSON string. */
const wireDecimal = z.union([z.number(), z.string()]).nullable();

const LauncherContactMembershipCampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** `launcher_sfdc_campaigns.start_date`, cast `::text` in the route SQL. */
  start_date: z.string().nullable(),
});

const LauncherContactMembershipMemberSchema = z.object({
  id: z.string(),
  cm_status: z.string().nullable(),
  has_responded: z.boolean().nullable(),
  /** `launcher_sfdc_campaign_members.created_date`, cast `::text`. */
  created_date: z.string().nullable(),
  contact_id: z.string().nullable(),
  contact_name: z.string().nullable(),
  contact_title: z.string().nullable(),
  contact_email: z.string().nullable(),
  account_name: z.string().nullable(),
  gtme_owner_name: z.string().nullable(),
  gs_owner_name: z.string().nullable(),
  claydr_name: z.string().nullable(),
  product_tier: z.string().nullable(),
  plan_interval: z.string().nullable(),
  arr_active_subscriptions: wireDecimal,
  sales_tier: z.string().nullable(),
  account_type: z.string().nullable(),
  /** `DATE` columns, all cast `::text` in the route SQL. */
  last_gtme_meeting_date: z.string().nullable(),
  last_gs_meeting_date: z.string().nullable(),
  last_claydr_meeting_date: z.string().nullable(),
  suppressed: z.boolean().nullable(),
  suppression_reasons: z.string().nullable(),
});

const LauncherContactMembershipEventSchema = z.object({
  /** `launcher_event_members.id` — `SERIAL`, never null once the row exists. */
  id: z.number(),
  role: z.string().nullable(),
  table_id: z.number().nullable(),
  /** `launcher_event_tables.table_name`, via a `LEFT JOIN` — null if `table_id` is null. */
  table_name: z.string().nullable(),
  attending_status: z.string().nullable(),
  dietary_restrictions: z.string().nullable(),
  cal_invite_sent: z.boolean().nullable(),
  cal_invite_accepted: z.boolean().nullable(),
  owner_exclusion: z.boolean().nullable(),
  owner_priority: z.boolean().nullable(),
  intel_notes: z.string().nullable(),
  goer_notes: z.string().nullable(),
  follow_up_owner: z.string().nullable(),
  primary_met_with: z.string().nullable(),
  secondary_met_with: z.string().nullable(),
  tertiary_met_with: z.string().nullable(),
  /** `DATE`, cast `::text` in the route SQL. */
  last_follow_up_date: z.string().nullable(),
  /**
   * Resolved via `LEFT JOIN salesforce_user` on the corresponding id column
   * above (`primary_met_with` etc., `follow_up_owner` for this one).
   * `salesforce_user.name` is nullable (`prisma/schema/core.prisma`'s
   * `SalesforceUser.name String?`) — an unmirrored or departed user, or a
   * null id, resolves to `null` rather than the raw SFDC id.
   */
  primary_met_with_name: z.string().nullable(),
  secondary_met_with_name: z.string().nullable(),
  tertiary_met_with_name: z.string().nullable(),
  follow_up_owner_name: z.string().nullable(),
});

export const LauncherContactMembershipSchema = z.object({
  campaign: LauncherContactMembershipCampaignSchema,
  member: LauncherContactMembershipMemberSchema,
  /** `null` when no `launcher_event_members` row exists for this CM yet. */
  event: LauncherContactMembershipEventSchema.nullable(),
});

export type LauncherContactMembership = z.infer<typeof LauncherContactMembershipSchema>;
